import { useEffect, useState } from 'react';

import { BankOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Switch,
} from 'antd';
import { useTranslation } from 'react-i18next';

import IntegrationCard from 'components/IntegrationCard/IntegrationCard';

import { ApiErrorData } from 'models/ApiResponse';
import { Company } from 'models/Company';
import { Country } from 'models/Country';
import { KitCompany } from 'models/KitCompany';
import {
  TiendaPerfectaCompaniesResponse,
  TiendaPerfectaCompany,
} from 'models/TiendaPerfecta';

import { CompanyService } from 'services/CompanyService';
import { KitCompanyService } from 'services/KitCompanyService';
import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { CompanyFormPayload } from '../types';
import KitIntegrationFields, {
  KIT_COMPANIES_FIELD,
} from './KitIntegrationFields';
import TiendaPerfectaIntegrationFields, {
  TIENDA_PERFECTA_COMPANY_FIELD,
} from './TiendaPerfectaIntegrationFields';

const I18N_BASE = 'scenes.settings.companies.components.detail_company_modal';

interface DetailCompanyModalProps {
  isOpen: boolean;
  id: number;
  handleModalCancel: () => void;
  submit: (id: number, payload: CompanyFormPayload) => void;
  countries: Country[];
}

function DetailCompanyModal({
  isOpen,
  handleModalCancel,
  submit,
  id,
  countries,
}: DetailCompanyModalProps) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [kitEnabled, setKitEnabled] = useState(false);
  const [tiendaPerfectaEnabled, setTiendaPerfectaEnabled] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [kitCompanies, setKitCompanies] = useState<KitCompany[]>([]);
  const [tiendaPerfectaCompanies, setTiendaPerfectaCompanies] = useState<
    TiendaPerfectaCompany[]
  >([]);
  /**
   * Code guardado que ya no existe en el catálogo de Tienda Perfecta. El
   * selector queda vacío —como si no tuviera compañía— y la validación obliga a
   * elegir otra; este code alimenta el aviso que explica por qué.
   */
  const [orphanCompanyCode, setOrphanCompanyCode] = useState<string | null>(
    null
  );
  const { t } = useTranslation();

  const selectedKitCompanyIds = Form.useWatch<number[]>(
    KIT_COMPANIES_FIELD,
    form
  );
  const selectedTiendaPerfectaCode = Form.useWatch<string>(
    TIENDA_PERFECTA_COMPANY_FIELD,
    form
  );

  useEffect(() => {
    form.resetFields();
    setKitEnabled(false);
    setTiendaPerfectaEnabled(false);
    setIsPrivate(false);
    setKitCompanies([]);
    setOrphanCompanyCode(null);

    if (isOpen) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isOpen]);

  const fetchData = async () => {
    setLoading(true);

    const response = await CompanyService.findById(id);

    if (!response.success) {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(`${I18N_BASE}.errors.fetch_company_failed`),
        3.5
      );
      handleModalCancel();
      setLoading(false);
      return;
    }

    const data = response.data as Company;
    form.setFieldValue('name', data.name);
    form.setFieldValue('country', data.countryCode);

    const kitIntegrationEnabled = data.kitIntegrationEnabled ?? false;
    setKitEnabled(kitIntegrationEnabled);
    setIsPrivate(data.isPrivate ?? false);

    const kitCompaniesResponse = await KitCompanyService.getByCountry(
      data.countryCode
    );

    if (kitCompaniesResponse.success) {
      setKitCompanies(kitCompaniesResponse.data as KitCompany[]);
    } else {
      const error = kitCompaniesResponse.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t('scenes.settings.companies.scene.errors.fetch_kit_companies'),
        3.5
      );
    }

    if (kitIntegrationEnabled && data.kitCompanies?.length) {
      form.setFieldValue(
        KIT_COMPANIES_FIELD,
        data.kitCompanies.map((c) => c.id)
      );
    }

    const tpEnabled = data.tiendaPerfectaIntegrationEnabled ?? false;
    setTiendaPerfectaEnabled(tpEnabled);

    if (tpEnabled) {
      const companiesResponse = await TiendaPerfectaService.getCompanies();
      const catalog = companiesResponse.success
        ? (companiesResponse.data as TiendaPerfectaCompaniesResponse)
            ?.companies ?? []
        : [];

      if (!companiesResponse.success) {
        const error = companiesResponse.data as ApiErrorData;
        message.error(
          (error.message as string) ||
            t(
              'scenes.settings.companies.scene.errors.fetch_tienda_perfecta_companies'
            ),
          3.5
        );
      }

      setTiendaPerfectaCompanies(catalog);

      const savedCode = data.tiendaPerfectaCompanyCode ?? null;
      const existsInCatalog =
        !!savedCode && catalog.some((c) => c.code === savedCode);

      if (existsInCatalog) {
        form.setFieldValue(TIENDA_PERFECTA_COMPANY_FIELD, savedCode);
      } else if (savedCode) {
        setOrphanCompanyCode(savedCode);
      }
    }

    setLoading(false);
  };

  const handleKitSwitchChange = (checked: boolean) => {
    setKitEnabled(checked);
    if (!checked) {
      form.setFieldValue(KIT_COMPANIES_FIELD, []);
    }
  };

  const handleTiendaPerfectaSwitchChange = async (checked: boolean) => {
    setTiendaPerfectaEnabled(checked);

    if (!checked) {
      form.setFieldValue(TIENDA_PERFECTA_COMPANY_FIELD, undefined);
      setOrphanCompanyCode(null);
      return;
    }

    // La compañía llegó con la integración apagada: el catálogo todavía no se
    // pidió porque no había nada que mostrar.
    if (tiendaPerfectaCompanies.length > 0) return;

    const response = await TiendaPerfectaService.getCompanies();

    if (response.success) {
      setTiendaPerfectaCompanies(
        (response.data as TiendaPerfectaCompaniesResponse)?.companies ?? []
      );
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) ||
          t(
            'scenes.settings.companies.scene.errors.fetch_tienda_perfecta_companies'
          ),
        3.5
      );
    }
  };

  const onFinish = async (values: any) => {
    setLoading(true);

    await submit(id, {
      name: values.name.trim(),
      isPrivate,
      kitIntegrationEnabled: kitEnabled,
      kitCompanyIds: kitEnabled ? values[KIT_COMPANIES_FIELD] ?? [] : [],
      tiendaPerfectaIntegrationEnabled: tiendaPerfectaEnabled,
      tiendaPerfectaCompanyCode: tiendaPerfectaEnabled
        ? values[TIENDA_PERFECTA_COMPANY_FIELD] ?? null
        : null,
    });

    setLoading(false);
  };

  return (
    <Modal
      title={t(`${I18N_BASE}.title`, { id })}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={560}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <IntegrationCard
          title={t(`${I18N_BASE}.sections.company_data`)}
          icon={<BankOutlined />}
        >
          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="name"
                label={t(`${I18N_BASE}.labels.name`)}
                style={{ marginBottom: 16 }}
                rules={[
                  {
                    required: true,
                    message: t(`${I18N_BASE}.validations.name.required`),
                  },
                ]}
              >
                <Input
                  type="text"
                  disabled={loading}
                  placeholder={t(`${I18N_BASE}.placeholders.name`)}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item
                name="country"
                label={t(`${I18N_BASE}.labels.country`)}
                style={{ marginBottom: 16 }}
              >
                <Select
                  placeholder={t(`${I18N_BASE}.placeholders.select_option`)}
                  options={countries.map((record) => ({
                    value: record.code,
                    label: record.name,
                  }))}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Switch
                  checked={isPrivate}
                  onChange={(checked) => setIsPrivate(checked)}
                  disabled={loading}
                />
                <span style={{ marginLeft: 10 }}>
                  {t(`${I18N_BASE}.labels.is_private_switch`)}
                </span>
              </Form.Item>
            </Col>
          </Row>
        </IntegrationCard>

        <KitIntegrationFields
          enabled={kitEnabled}
          onEnabledChange={handleKitSwitchChange}
          kitCompanies={kitCompanies}
          kitCompaniesLoading={loading}
          disabled={loading}
          selectedCompanyIds={selectedKitCompanyIds ?? []}
        />

        <TiendaPerfectaIntegrationFields
          enabled={tiendaPerfectaEnabled}
          onEnabledChange={handleTiendaPerfectaSwitchChange}
          companies={tiendaPerfectaCompanies}
          companiesLoading={loading}
          disabled={loading}
          orphanCompanyCode={orphanCompanyCode}
          selectedCompanyCode={selectedTiendaPerfectaCode ?? null}
        />

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(`${I18N_BASE}.buttons.save_changes`)}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default DetailCompanyModal;
